import React, { useState, useEffect, useCallback } from 'react';
import { Dumbbell, Plus, Trash2, Pencil, Loader2, Sparkles, Download, Copy, UserPlus, MessageCircle, Mail, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { AIWorkoutGeneratorModal } from './AIWorkoutGeneratorModal';
import { AIDraftParserModal } from './AIDraftParserModal';
import { ProgramEditorForm, blankProgram } from './ProgramEditorForm';
import { isSessionExpired } from '../../utils/adminSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ProgramsProEditor = ({ email, csrfToken, clients }) => {
  const fetchWithCsrf = (url, options = {}) => fetch(url, {
    ...options, credentials: 'include',
    headers: { ...options.headers, 'X-CSRF-Token': csrfToken },
  });
  const jsonHeaders = { 'Content-Type': 'application/json' };

  const [programs, setPrograms] = useState(null);
  const [setTypes, setSetTypes] = useState([]);
  const [library, setLibrary] = useState({ muscle_groups: [], exercises: [] });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [pdfBusyId, setPdfBusyId] = useState(null);
  const [waBusyId, setWaBusyId] = useState(null);
  const [emailBusyId, setEmailBusyId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [duplicateEmail, setDuplicateEmail] = useState('');
  const [duplicateBusy, setDuplicateBusy] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [consumeRequestCredit, setConsumeRequestCredit] = useState(true);

  const load = useCallback(() => {
    fetch(`${BACKEND_URL}/api/admin/programs-pro?email=${encodeURIComponent(email)}`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setPrograms(d.programs || []))
      .catch(() => { setPrograms([]); toast.error('Échec du chargement des programmes.'); });
  }, [email]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/programs-pro/set-types`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setSetTypes(d.set_types || [])).catch(() => {});
    fetch(`${BACKEND_URL}/api/admin/programs-pro/exercise-library`, { credentials: 'include' })
      .then((r) => r.json()).then(setLibrary).catch(() => {});
  }, []);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/program-requests`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then((d) => setPendingRequest((d || {}).pending_request || null)).catch(() => {});
  }, [email]);

  const upd = (mut) => setEditing((e) => { const c = JSON.parse(JSON.stringify(e)); mut(c); return c; });

  const save = () => {
    if (!editing.name.trim()) { toast.error('Le nom du programme est requis.'); return; }
    if (!editing.goal.trim()) { toast.error("L'objectif est requis."); return; }
    setBusy(true);
    const isNew = !editing.program_id;
    const url = isNew
      ? `${BACKEND_URL}/api/admin/programs-pro`
      : `${BACKEND_URL}/api/admin/programs-pro/${editing.program_id}`;
    const body = isNew ? { ...editing, email, consume_request_credit: consumeRequestCredit } : editing;
    fetchWithCsrf(url, { method: isNew ? 'POST' : 'PUT', headers: jsonHeaders, body: JSON.stringify(body) })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then(() => {
        setEditing(null); load(); toast.success('Programme enregistré !');
        if (isNew && pendingRequest) setPendingRequest(null);
      })
      .catch((r) => { if (r) toast.error("Échec de l'enregistrement."); })
      .finally(() => setBusy(false));
  };

  const del = (id) => {
    if (!window.confirm('Supprimer définitivement ce programme et tout son historique ?')) return;
    fetchWithCsrf(`${BACKEND_URL}/api/admin/programs-pro/${id}`, { method: 'DELETE' })
      .then((r) => { if (isSessionExpired(r)) return; load(); toast.success('Programme supprimé.'); });
  };

  const downloadPdf = (p) => {
    setPdfBusyId(p.program_id);
    fetch(`${BACKEND_URL}/api/admin/programs-pro/${p.program_id}/pdf`, { credentials: 'include' })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.blob() : Promise.reject(r);
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `programme-${p.name.replace(/\s+/g, '-').toLowerCase()}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((r) => { if (r) toast.error('Échec du téléchargement du PDF.'); })
      .finally(() => setPdfBusyId(null));
  };

  const duplicate = (programId) => {
    if (!duplicateEmail) { toast.error('Choisissez un client.'); return; }
    setDuplicateBusy(true);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/programs-pro/${programId}/duplicate`, {
      method: 'POST', headers: jsonHeaders, body: JSON.stringify({ email: duplicateEmail }),
    })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then(() => {
        toast.success(`Programme dupliqué vers ${duplicateEmail} !`);
        setDuplicatingId(null); setDuplicateEmail('');
        if (duplicateEmail === email) load();
      })
      .catch((r) => { if (r) toast.error('Échec de la duplication.'); })
      .finally(() => setDuplicateBusy(false));
  };

  const shareOnWhatsApp = (p) => {
    setWaBusyId(p.program_id);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/programs-pro/${p.program_id}/pdf/whatsapp-link`, {
      method: 'POST', headers: jsonHeaders, body: JSON.stringify({ origin: BACKEND_URL }),
    })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then((d) => {
        if (!d.phone_found) toast.info("Numero du client introuvable, choisissez le contact dans WhatsApp.");
        window.open(d.whatsapp_url, '_blank');
      })
      .catch((r) => { if (r) toast.error("Echec de la preparation du partage WhatsApp."); })
      .finally(() => setWaBusyId(null));
  };

  const emailPdf = (p) => {
    setEmailBusyId(p.program_id);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/programs-pro/${p.program_id}/pdf/email`, { method: 'POST' })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then(() => toast.success(`PDF envoyé par email à ${p.email} !`))
      .catch((r) => { if (r) toast.error("Échec de l'envoi de l'email."); })
      .finally(() => setEmailBusyId(null));
  };


  return (
    <div className="rounded-2xl bg-zinc-900/60 border border-white/10 p-5" data-testid="programs-pro-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Dumbbell className="w-5 h-5 text-primary" /> Programmes Pro
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowDraftModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-emerald-600 to-teal-500 text-white text-sm font-bold rounded-xl hover:scale-105 transition-transform"
            data-testid="ai-draft-parser-btn"
          >
            <FileText className="w-4 h-4" /> Brouillon IA
          </button>
          <button
            onClick={() => setShowAIModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-sm font-bold rounded-xl hover:scale-105 transition-transform"
            data-testid="ai-generate-program-btn"
          >
            <Sparkles className="w-4 h-4" /> Générer avec l'IA
          </button>
          <button
            onClick={() => { setEditing(blankProgram()); setConsumeRequestCredit(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-primary to-pink-500 text-white text-sm font-bold rounded-xl hover:scale-105 transition-transform"
            data-testid="new-program-pro-btn"
          >
            <Plus className="w-4 h-4" /> Nouveau programme
          </button>
        </div>
      </div>

      {showDraftModal && (
        <AIDraftParserModal
          csrfToken={csrfToken}
          onClose={() => setShowDraftModal(false)}
          onGenerated={(program) => { setEditing(program); setConsumeRequestCredit(true); setShowDraftModal(false); toast.success('Brouillon analysé ! Vérifiez-le puis enregistrez.'); }}
        />
      )}

      {showAIModal && (
        <AIWorkoutGeneratorModal
          csrfToken={csrfToken}
          clientEmail={email}
          onClose={() => setShowAIModal(false)}
          onGenerated={(program) => { setEditing(program); setConsumeRequestCredit(true); setShowAIModal(false); toast.success('Programme généré ! Vérifiez-le puis enregistrez.'); }}
        />
      )}

      {!editing && (
        <div className="space-y-2">
          {programs === null ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-primary animate-spin" /></div>
          ) : programs.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4 text-center">Aucun programme pro pour ce client.</p>
          ) : null}
          {(programs || []).map((p) => (
            <div key={p.program_id} className="bg-zinc-800/60 border border-white/10 rounded-xl px-4 py-3" data-testid={`program-pro-card-${p.program_id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-white font-semibold">{p.name}</p>
                    {p.author_type === 'client' && <span className="px-1.5 py-0.5 rounded text-[9px] bg-blue-500/15 text-blue-300 border border-blue-500/25 font-bold">CRÉÉ PAR LE CLIENT</span>}
                  </div>
                  <p className="text-xs text-zinc-500">{p.goal} · {p.sessions.length} séance(s) type · {p.weeks_target} sem. · objectif tonnage +{p.tonnage_growth_target_pct}%</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => downloadPdf(p)} disabled={pdfBusyId === p.program_id} className="p-2 text-zinc-400 hover:text-emerald-400 transition-colors disabled:opacity-50" title="Télécharger le PDF" data-testid={`download-pdf-${p.program_id}`}>
                    {pdfBusyId === p.program_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  </button>
                  <button onClick={() => shareOnWhatsApp(p)} disabled={waBusyId === p.program_id} className="p-2 text-zinc-400 hover:text-green-400 transition-colors disabled:opacity-50" title="Partager le PDF via WhatsApp" data-testid={`whatsapp-share-btn-${p.program_id}`}>
                    {waBusyId === p.program_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
                  </button>
                  <button onClick={() => emailPdf(p)} disabled={emailBusyId === p.program_id} className="p-2 text-zinc-400 hover:text-sky-400 transition-colors disabled:opacity-50" title="Envoyer le PDF par email" data-testid={`email-pdf-btn-${p.program_id}`}>
                    {emailBusyId === p.program_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  </button>
                  <button onClick={() => { setDuplicatingId(duplicatingId === p.program_id ? null : p.program_id); setDuplicateEmail(''); }} className="p-2 text-zinc-400 hover:text-violet-400 transition-colors" title="Dupliquer vers un client" data-testid={`duplicate-program-btn-${p.program_id}`}>
                    <Copy className="w-4 h-4" />
                  </button>
                  <button onClick={() => setEditing(JSON.parse(JSON.stringify(p)))} className="p-2 text-zinc-400 hover:text-primary transition-colors" data-testid={`edit-program-pro-${p.program_id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(p.program_id)} className="p-2 text-zinc-400 hover:text-red-400 transition-colors" data-testid={`delete-program-pro-${p.program_id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              {duplicatingId === p.program_id && (
                <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2 flex-wrap" data-testid={`duplicate-form-${p.program_id}`}>
                  <select value={duplicateEmail} onChange={(e) => setDuplicateEmail(e.target.value)}
                    className="flex-1 min-w-[180px] bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid={`duplicate-select-${p.program_id}`}>
                    <option value="">Choisir un client...</option>
                    {(clients || []).map((c) => <option key={c.email} value={c.email}>{c.name ? `${c.name} — ${c.email}` : c.email}</option>)}
                  </select>
                  <button onClick={() => duplicate(p.program_id)} disabled={duplicateBusy}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-sm font-bold rounded-lg hover:scale-105 transition-transform disabled:opacity-50"
                    data-testid={`confirm-duplicate-${p.program_id}`}>
                    {duplicateBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Dupliquer
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ProgramEditorForm
          editing={editing} upd={upd} library={library} setTypes={setTypes} csrfToken={csrfToken}
          onSave={save} onCancel={() => setEditing(null)} busy={busy}
          pendingRequestBanner={!editing.program_id && pendingRequest ? (
            <div className="rounded-xl bg-blue-500/10 border border-blue-500/25 p-3.5" data-testid="pending-request-fulfill-banner">
              <p className="text-xs font-bold text-blue-300 uppercase tracking-wide mb-1.5">Ce client a une demande de programme en attente</p>
              {pendingRequest.carte_blanche ? (
                <p className="text-sm text-zinc-300 mb-2">Carte blanche : le client vous laisse décider entièrement.</p>
              ) : (
                <div className="text-sm text-zinc-300 space-y-1 mb-2">
                  {pendingRequest.satisfaction && <p>Satisfaction actuelle : <span className="text-white font-semibold">{pendingRequest.satisfaction}/5</span></p>}
                  {pendingRequest.liked && <p>Aimé : <span className="text-white">{pendingRequest.liked}</span></p>}
                  {pendingRequest.disliked && <p>Pas aimé : <span className="text-white">{pendingRequest.disliked}</span></p>}
                  {pendingRequest.preferences && <p>Préférences : <span className="text-white">{pendingRequest.preferences}</span></p>}
                </div>
              )}
              <label className="flex items-center gap-2.5 cursor-pointer" data-testid="consume-request-credit-toggle">
                <input type="checkbox" checked={consumeRequestCredit} onChange={(e) => setConsumeRequestCredit(e.target.checked)} className="w-4 h-4 accent-primary" />
                <span className="text-sm text-white">{consumeRequestCredit ? 'Décompter le crédit du client' : 'Offrir ce programme gratuitement (crédit conservé)'}</span>
              </label>
            </div>
          ) : null}
        />
      )}
    </div>
  );
};

export default ProgramsProEditor;
